<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlns:xlink="http://www.w3.org/1999/xlink"
    :class="{ 'svg-icon': true, disabled: disabled }"
    :style="iconStyle"
    :key="route.fullPath"
    v-bind="$attrs"
    aria-hidden="true"
    @click="handleClick"
  >
    <use :href="iconUrl" :xlink:href="iconUrl"></use>
  </svg>
</template>
<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();

defineOptions({
  name: 'SvgIcon',
});

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    required: true,
  },
  size: {
    type: [String, Number],
    default: undefined,
  },
  color: {
    type: String,
    default: undefined,
  },
});
const emit = defineEmits(['click']);

// Check if we need full path for older Chrome/WebKit versions
const needsFullPath = computed(() => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;
  const chromeMatch = userAgent.match(/Chrome\/(\d+)\./);

  if (chromeMatch) {
    const chromeVersion = parseInt(chromeMatch[1], 10);
    return chromeVersion < 78;
  }

  return false;
});

// TV Fix: Compute the full URL path to fix <base> tag/Router issues on older WebKit.
// On WebOS/file: origin, use fragment-only to avoid GET file://... (ERR_INVALID_URL).
const iconUrl = computed(() => {
  const iconId = `#icon-${props.icon}`;

  if (typeof window === 'undefined') {
    return iconId;
  }

  if (window.location.protocol === 'file:') {
    return iconId;
  }

  if (needsFullPath.value) {
    const currentPath = route.fullPath;
    const baseUrl = `${window.location.origin}${currentPath}`;
    const requestUrl = baseUrl.split('#')[0];
    return `${requestUrl}${iconId}`;
  }

  return iconId;
});

const iconStyle = computed(() => {
  const style = {};
  if (props.size) {
    // Ensure units are handled; TVs often prefer explicit px or rems
    style.width = typeof props.size === 'string' ? props.size : `${props.size / 128}rem`;
    style.height = typeof props.size === 'string' ? props.size : `${props.size / 128}rem`;
  }
  if (props.color) {
    style.color = props.color;
  }
  return style;
});

function handleClick(event) {
  if (props.disabled) return;
  emit('click', event);
}
</script>
<style lang="less" scoped>
.svg-icon {
  display: inline-block;
  vertical-align: middle;
  width: 1em;
  height: 1em;
  fill: currentColor;
  stroke: none;
  // TV Fix: rendering hint for older GPU compositors
  will-change: transform;
  overflow: hidden;
  cursor: pointer;

  // Ensure use element inherits fill for older browsers
  use {
    fill: inherit;
    // TV Fix: Explicitly disable pointer events on the internal use tag
    // to prevent click-through issues on some touch/remote interfaces
    pointer-events: none;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5; // Visual feedback for disabled state
  }

  &:focus {
    outline: none;
  }
}
</style>
